import { useState } from "react";
import { useCreatePortfolio, useEditPortfolio } from "../mutations";
import { validationRules } from "../../../shared/utils/validationRules";
import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";

export const useAddPortfolio = ({
  isEdit,
  portfolioId,
}: {
  isEdit: boolean;
  portfolioId?: string;
}) => {
  const createPortfolio = useCreatePortfolio();
  const editPortfolio = useEditPortfolio();

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>(
    {},
  );

  const [submitMessage, setSubmitMessage] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<{
    programme: AutocompleteOption | null; 
    portfolio_name: string | null;
    description: string | null;
    start_date: string | null;
    end_date: string | null;
  }>({
    programme: null,
    portfolio_name: null,
    description: null,
    start_date: null,
    end_date: null,
  });

  const validationConfig: Record<string, string[]> = {
    programme: ["is_empty"],
    portfolio_name: ["is_empty"],
    description: ["is_empty"],
    start_date: ["is_empty"],
    end_date: ["is_empty"],
  };

  const validateField = (key: string, value: string): string | null => {
    const rules = validationConfig[key];
    if (!rules) return null;
    for (const ruleName of rules) {
      const rule = validationRules[ruleName];
      if (rule) {
        const error = rule(value);
        if (error) return error;
      }
    }
    return null;
  };

  const validateAllFields = (): { isValid: boolean, errors: any } => {
    let newErrors: Record<string, string | null> = {};

    Object.keys(validationConfig).forEach((key) => {
      const error = validateField(key, (formData as any)[key]);
      if (error) newErrors[key] = error;
    });
    setFieldErrors(newErrors);

    const isValid = Object.values(newErrors).every((error) => !error);

    return { isValid, errors: newErrors };
  };

  const handleFieldChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    const error = validateField(key, value);
    setFieldErrors((prev) => ({ ...prev, [key]: error }));
  };

  const handleSubmit = async () => {

    setSubmitMessage(null);
    setIsSubmitting(true);

    const { isValid, errors } = validateAllFields();    

    if (!isValid) {
      console.warn("Validation Failed:", errors);
      setIsSubmitting(false);
      return;
    }

    const formatDate = (dateString: string | null): string | null => {
      if (!dateString) return null;
      const date = new Date(dateString);
      return date.toISOString();
    };

    const payload = {
      programme_id: formData?.programme?.id,
      portfolio_name: formData?.portfolio_name ?? "",
      description: formData?.description ?? "",
      start_date: formatDate(formData?.start_date),
      end_date: formatDate(formData?.end_date),
    };

    try {
      if (isEdit && portfolioId) {
        await editPortfolio.mutateAsync({ portfolioId, payload });
      } else {
        await createPortfolio.mutateAsync({ payload });
      }
      setSubmitMessage({
        type: "success",
        message: "Portfolio created successfully!",
      });
      setFormData({
        programme: null,
        portfolio_name: null,
        description: null,
        start_date: null,
        end_date: null,
      });
    } catch (error: any) {
      setSubmitMessage({
        type: "error",
        message:
          error?.response?.data?.message ||
          "failed to create portfolio. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    fieldErrors,
    setFormData,
    handleFieldChange,
    isSubmitting,
    handleSubmit,
    submitMessage,
    setSubmitMessage
  };
};
